import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useContext,
} from 'react';
import {
  Form,
  notification,
  Popconfirm,
  Spin,
  Typography,
  Button as AntButton,
  Input,
} from 'antd';
import DF from 'DisplayFramework';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { indexBy, shouldUpdateCompare } from 'utils/utils2';
import {} from 'services/Document';
import {
  ParMarketingLabelResponse,
  ParMarketingResponse,
  RemoveParMarketingLabel,
  UpdateParMarketingLabel,
} from 'services/assetconfig';
import { useFormList } from 'hooks/useFormList';
import { PlusOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import AssetUpload from 'components/AssetUpload';
import MarketingLabelModal from './MarketingLabelModal';
import ModalController from 'components/ModalController';
import Button from 'components/Button';
// import SaveButtonAction from 'components/SaveButtonAction';
import DeleteButton from 'components/DeleteButton';
import { getAssetCatalogByID } from 'services/assetconfig';
import { useQuery } from 'react-query';
import ImagePreviw from '../AssetCatalog/components/ImagePreview';
import UpdateButton from 'components/UpdateButtonAction';
import { AuthContext } from 'components/Auth/Auth';

export type MarketingLabelDetailProps = {
  data: Array<ParMarketingLabelResponse>;
  mc: ParMarketingResponse;
  isFetching: any;
  refetch: () => void;
  catalogid?: number;
  idOrCreate?: string;
};

const MarketingLabelList = ({
  data,
  isFetching,
  mc,
  refetch,
  idOrCreate,
}: MarketingLabelDetailProps) => {
  const [form] = Form.useForm();
  const fieldName = 'label';
  const [loading, setLoading] = useState(false);
  const [isCreate, setIsCreate] = useState(idOrCreate === 'create');
  const lancode = !isCreate ? idOrCreate : undefined;
  const [editingKeys, setEditingKeys] = useState<string | null>();
  const isEditable = (id: string): boolean => editingKeys !== id;
  const { isInspect } = useContext(AuthContext);

  const { remove } = useFormList(form, fieldName, {
    addAsFirst: true,
    idKey: 'lancode',
  });

  const { data: catalog } = useQuery(
    ['getAssetCatalogByID', { catalogid: lancode }],
    () =>
      getAssetCatalogByID({ catalogid: Number(lancode) }).then(
        (res) => res.data
      ),
    {
      enabled: !!lancode && !isCreate,
    }
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data.map((el) => ({
          ...el,
          mcid: el.mclabelPK.mcid,
          lancode: el.mclabelPK.lancode,
          picturebuttomlefturl: el.picturebuttomlefturl,
          picturebuttomrighturl: el.picturebuttomrighturl,
          picturecenterurl: el.picturecenterurl,
          picturemobileurl: el.picturemobileurl,
          picturetoplefturl: el.picturetoplefturl,
          picturetoprighturl: el.picturetoprighturl,
          pictureurl: el.pictureurl,
        })),
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data, catalog]);

  const getName = useCallback(
    (name: string) => (record: ParMarketingLabelResponse) => {
      const labels = form.getFieldValue(fieldName);
      const index = indexBy(labels, 'lancode')[record.mclabelPK.lancode];

      return [fieldName, index, name];
    },
    [form]
  );

  const onSaveOrEdit = useCallback(
    (record: ParMarketingLabelResponse) => {
      UpdateParMarketingLabel({
        request: {
          ...record,
          picturebuttomlefturl: form.getFieldValue(
            getName('picturebuttomlefturl')(record)
          ),
          picturebuttomrighturl: form.getFieldValue(
            getName('picturebuttomrighturl')(record)
          ),
          picturecenterurl: form.getFieldValue(
            getName('picturecenterurl')(record)
          ),
          picturemobileurl: form.getFieldValue(
            getName('picturemobileurl')(record)
          ),
          picturetoplefturl: form.getFieldValue(
            getName('picturetoplefturl')(record)
          ),
          picturetoprighturl: form.getFieldValue(
            getName('picturetoprighturl')(record)
          ),
          pictureurl: form.getFieldValue(getName('pictureurl')(record)),
        },
        mcid: record.mclabelPK.mcid,
      }).then((response) => {
        if (response.code === '200') {
          notification.success({
            description: response.description,
            message: response.message,
          });
          refetch();
          setEditingKeys(null);
        }
      });
    },

    [form, refetch]
  );

  const columns = useMemo<DataGridColDef<ParMarketingLabelResponse>[]>(
    () => [
      {
        dataIndex: 'mcid',
        fixed: 'left',
        width: 100,
        render: (value, record) => {
          return (
            <Form.Item name={getName('mcid')(record)} noStyle>
              {value}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'lancode',
        width: 100,
        render: (value, record) => {
          return (
            <Form.Item name={getName('lancode')(record)} noStyle>
              {value}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'mclabel',
        width: 200,
        render: (value, record) => {
          return isEditable(record.mclabelPK.lancode) ? (
            <Form.Item name={getName('mcid')(record)} noStyle>
              <ModalController>
                {({ setVisible, visible }) => (
                  <>
                    <a onClick={() => setVisible(true)}>{value}</a>
                    <MarketingLabelModal
                      visible={visible}
                      onCancel={() => {
                        setVisible(false);
                      }}
                      onSuccess={() => {
                        refetch();
                        setVisible(false);
                      }}
                      idOrCreate={String(record.mclabelPK.lancode)}
                      ParMarketingLabel={record}
                      ParMarketingResponse={mc}
                    />
                  </>
                )}
              </ModalController>
            </Form.Item>
          ) : (
            <Form.Item name={getName('mclabel')(record)} noStyle>
              <Input />
            </Form.Item>
          );
        },
      },

      {
        dataIndex: 'mcdescription',
        width: 300,
        render: (value, record) => {
          return isEditable(record.mclabelPK.lancode) ? (
            <Form.Item name={getName('mcdescription')(record)} noStyle>
              <Typography.Paragraph
                ellipsis={{
                  rows: 2,
                  expandable: true,
                  symbol: 'more',
                }}
              >
                {value}
              </Typography.Paragraph>
            </Form.Item>
          ) : (
            <Form.Item name={getName('mcdescription')(record)} noStyle>
              <Input.TextArea />
            </Form.Item>
          );
        },
      },

      {
        dataIndex: 'picturebuttomlefturl',
        render: (_value, _record) => {
          const name = [
            fieldName,
            indexBy(form.getFieldValue(fieldName), 'lancode')[
              _record.mclabelPK.lancode
            ],
            'picturebuttomlefturl',
          ];
          return !isEditable(_record.mclabelPK.lancode) ? (
            <AssetUpload
              simpleUpload
              namePath={name}
              form={form}
              objectreference={form.getFieldValue('assetreference')}
            />
          ) : (
            <ImagePreviw
              fieldName={fieldName}
              form={form}
              _record={_record}
              name="picturebuttomlefturl"
              getNameProps={getName}
            />
          );
        },
        width: 180,
      },

      {
        dataIndex: 'picturebuttomrighturl',
        render: (_value, _record) => {
          const name = [
            fieldName,
            indexBy(form.getFieldValue(fieldName), 'lancode')[
              _record.mclabelPK.lancode
            ],
            'picturebuttomrighturl',
          ];

          return !isEditable(_record.mclabelPK.lancode) ? (
            <AssetUpload
              simpleUpload
              namePath={name}
              form={form}
              objectreference={form.getFieldValue('assetreference')}
            />
          ) : (
            <ImagePreviw
              fieldName={fieldName}
              form={form}
              _record={_record}
              name="picturebuttomrighturl"
              getNameProps={getName}
            />
          );
        },
        width: 180,
      },

      {
        dataIndex: 'picturecenterurl',
        render: (_value, _record) => {
          const name = [
            fieldName,
            indexBy(form.getFieldValue(fieldName), 'lancode')[
              _record.mclabelPK.lancode
            ],
            'picturecenterurl',
          ];
          return !isEditable(_record.mclabelPK.lancode) ? (
            <AssetUpload
              simpleUpload
              namePath={name}
              form={form}
              objectreference={form.getFieldValue('assetreference')}
            />
          ) : (
            <ImagePreviw
              fieldName={fieldName}
              form={form}
              _record={_record}
              name="picturecenterurl"
              getNameProps={getName}
            />
          );
        },
        width: 180,
      },

      {
        dataIndex: 'picturemobileurl',
        render: (_value, _record) => {
          const name = [
            fieldName,
            indexBy(form.getFieldValue(fieldName), 'lancode')[
              _record.mclabelPK.lancode
            ],
            'picturemobileurl',
          ];
          return !isEditable(_record.mclabelPK.lancode) ? (
            <AssetUpload
              simpleUpload
              namePath={name}
              form={form}
              objectreference={form.getFieldValue('assetreference')}
            />
          ) : (
            <ImagePreviw
              fieldName={fieldName}
              form={form}
              _record={_record}
              name="picturemobileurl"
              getNameProps={getName}
            />
          );
        },
        width: 180,
      },

      {
        dataIndex: 'picturetoplefturl',
        render: (_value, _record) => {
          const name = [
            fieldName,
            indexBy(form.getFieldValue(fieldName), 'lancode')[
              _record.mclabelPK.lancode
            ],
            'picturetoplefturl',
          ];
          return !isEditable(_record.mclabelPK.lancode) ? (
            <AssetUpload
              simpleUpload
              namePath={name}
              form={form}
              objectreference={form.getFieldValue('assetreference')}
            />
          ) : (
            <ImagePreviw
              fieldName={fieldName}
              form={form}
              _record={_record}
              name="picturetoplefturl"
              getNameProps={getName}
            />
          );
        },
        width: 180,
      },

      {
        dataIndex: 'picturetoprighturl',
        render: (_value, _record) => {
          const name = [
            fieldName,
            indexBy(form.getFieldValue(fieldName), 'lancode')[
              _record.mclabelPK.lancode
            ],
            'picturetoprighturl',
          ];
          return !isEditable(_record.mclabelPK.lancode) ? (
            <AssetUpload
              simpleUpload
              namePath={name}
              form={form}
              objectreference={form.getFieldValue('assetreference')}
            />
          ) : (
            <ImagePreviw
              fieldName={fieldName}
              form={form}
              _record={_record}
              name="picturetoprighturl"
              getNameProps={getName}
            />
          );
        },
        width: 180,
      },

      {
        dataIndex: 'pictureurl',
        render: (_value, _record) => {
          const name = [
            fieldName,
            indexBy(form.getFieldValue(fieldName), 'lancode')[
              _record.mclabelPK.lancode
            ],
            'pictureurl',
          ];
          return !isEditable(_record.mclabelPK.lancode) ? (
            <AssetUpload
              simpleUpload
              namePath={name}
              form={form}
              objectreference={form.getFieldValue('assetreference')}
            />
          ) : (
            <ImagePreviw
              fieldName={fieldName}
              form={form}
              _record={_record}
              name="pictureurl"
              getNameProps={getName}
            />
          );
        },
        width: 180,
      },

      {
        key: 'actions',
        comcode: 'actions',
        fixed: 'right',
        render: (_, record) =>
          editingKeys === record?.mclabelPK.lancode ? (
            <>
              <UpdateButton
                comcode="update"
                htmlType="button"
                size="middle"
                onClick={(item) => {
                  item.preventDefault();
                  !isInspect && onSaveOrEdit(record);
                }}
              />
              <AntButton
                danger
                htmlType="button"
                icon={
                  <CloseOutlined height="14px" width="14px" color="white" />
                }
                onClick={() => {
                  !isInspect && setEditingKeys(null);
                }}
              />
            </>
          ) : (
            <>
              <Button
                htmlType="button"
                size="middle"
                comcode="edit"
                onClick={() => {
                  !isInspect && setEditingKeys(record.mclabelPK.lancode);
                }}
                icon={<EditOutlined />}
                style={{ width: '115px', height: '38px' }}
              />
              {/* <SaveButtonAction
                onClick={(item) => {
                  item.preventDefault();
                  onSaveOrEdit(record);
                }}
                size="small"
                comcode="update"
              /> */}
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  remove(record.mclabelPK.mcid);
                  RemoveParMarketingLabel({
                    mcid: record.mclabelPK.mcid,
                    lancode: record.mclabelPK.lancode,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ),
        width: 200,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getName, isFetching, onSaveOrEdit, refetch, editingKeys]
  );

  return (
    <DF.Provider pagcode="marketingCompaignLabel">
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Form.Item noStyle shouldUpdate={shouldUpdateCompare(fieldName)}>
            {({ getFieldValue }) => {
              const ControleConfig: ParMarketingLabelResponse[] =
                getFieldValue(fieldName);

              return (
                <DataGrid
                  rowKey="lancode"
                  dataSource={ControleConfig}
                  titleComcode="marketingLabelList"
                  columns={columns}
                  hideFilters
                  isLoading={isFetching}
                  extraActions={
                    <ModalController>
                      {({ setVisible, visible }) => (
                        <>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              setVisible(true);
                            }}
                            style={{ flex: 'right' }}
                            comcode="Add"
                          />
                          <MarketingLabelModal
                            idOrCreate="create"
                            visible={visible}
                            onCancel={() => {
                              setVisible(false);
                            }}
                            onSuccess={() => {
                              refetch();
                              setVisible(false);
                            }}
                            ParMarketingLabel={null}
                            ParMarketingResponse={mc}
                            list={ControleConfig}
                          />
                        </>
                      )}
                    </ModalController>
                  }
                />
              );
            }}
          </Form.Item>
        </Form>
      </Spin>
    </DF.Provider>
  );
};

export default MarketingLabelList;
